import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { amcAPI } from '../services/api';
import { Building2, AlertCircle } from 'lucide-react';

const Register = () => {
  const navigate = useNavigate();
  const { register, isAuthenticated } = useAuth();
  const [amcs, setAmcs] = useState([]);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'manager',
    amc_id: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCreateAMC, setShowCreateAMC] = useState(false);
  const [newAMCData, setNewAMCData] = useState({
    name: '',
    email: '',
    phone: '',
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
    fetchAMCs();
  }, [isAuthenticated, navigate]);

  const fetchAMCs = async () => {
    try {
      const response = await amcAPI.getAll();
      setAmcs(response.data);
    } catch (err) {
      console.error('Failed to fetch AMCs:', err);
    }
  };

  const handleCreateAMC = async () => {
    try {
      const response = await amcAPI.create(newAMCData);
      setAmcs([...amcs, response.data]);
      setFormData({ ...formData, amc_id: response.data.id, role: 'admin' });
      setShowCreateAMC(false);
      setNewAMCData({ name: '', email: '', phone: '' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create AMC');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await register(formData);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
              <Building2 size={32} className="text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Create Account</h1>
            <p className="text-gray-600 mt-2">Join your AMC or create a new one</p>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <AlertCircle className="text-red-600 mr-2 flex-shrink-0 mt-0.5" size={20} />
              <span className="text-red-800 text-sm">{error}</span>
            </div>
          )}

          {!showCreateAMC ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="email-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="password-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">AMC</label>
                <select
                  required
                  value={formData.amc_id}
                  onChange={(e) => setFormData({ ...formData, amc_id: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="amc-select"
                >
                  <option value="">Select an AMC</option>
                  {amcs.map((amc) => (
                    <option key={amc.id} value={amc.id}>{amc.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowCreateAMC(true)}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-700"
                >
                  + Create New AMC
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                <select
                  required
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="role-select"
                >
                  <option value="admin">Admin</option>
                  <option value="manager">Manager</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium
                  hover:bg-blue-700 disabled:bg-blue-400 transition-colors"
                data-testid="register-button"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>
          ) : (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Create New AMC</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">AMC Name</label>
                <input
                  type="text"
                  required
                  value={newAMCData.name}
                  onChange={(e) => setNewAMCData({ ...newAMCData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">AMC Email</label>
                <input
                  type="email"
                  required
                  value={newAMCData.email}
                  onChange={(e) => setNewAMCData({ ...newAMCData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone (Optional)</label>
                <input
                  type="tel"
                  value={newAMCData.phone}
                  onChange={(e) => setNewAMCData({ ...newAMCData, phone: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={handleCreateAMC}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700"
                >
                  Create AMC
                </button>
                <button
                  onClick={() => setShowCreateAMC(false)}
                  className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <p className="mt-6 text-center text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
